import { createContext, useContext, useEffect, useState } from "react";
import { promptId } from "@/schema/promptId";
import type { PromptBlock, Slide } from "@/schema";

/**
 * The presenting screen's "scan to copy" control for prompt blocks.
 *
 * Provided only by the screen that presents (DeckRuntime). Every other surface
 * that renders a slide (the remote's preview, the gallery, tests) has none, so a
 * prompt card there shows no QR button: the QR belongs to the projector.
 */
export interface PromptQr {
  /** The public id of this block, once known; undefined hides the button. */
  idFor: (b: PromptBlock) => string | undefined;
  open: (id: string) => void;
}

export const PromptQrContext = createContext<PromptQr | null>(null);
export const usePromptQr = () => useContext(PromptQrContext);

/** The current slide's prompt blocks, in order, each with its public id. */
export function usePromptIds(slug: string, slide: Slide | undefined): { block: PromptBlock; id: string }[] {
  const [found, setFound] = useState<{ key: string; list: { block: PromptBlock; id: string }[] }>({ key: "", list: [] });
  const key = slide ? `${slug}\0${slide.id}` : "";

  useEffect(() => {
    if (!slide) return;
    let alive = true;
    const prompts = slide.blocks
      .map((b, i) => ({ b, i }))
      .filter((x): x is { b: PromptBlock; i: number } => x.b.kind === "prompt");
    void Promise.all(prompts.map(({ b, i }) => promptId(slug, slide.id, i).then((id) => ({ block: b, id }))))
      .then((list) => {
        if (alive) setFound({ key, list: list.filter((x): x is { block: PromptBlock; id: string } => x.id !== null) });
      });
    return () => { alive = false; };
  }, [slug, slide, key]);

  // Derived, not stored per slide: a stale list from the previous slide is never shown.
  return found.key === key ? found.list : [];
}
